import React from 'react';
import PropTypes from 'prop-types';
import './Logo.css';

// Tenta importar a imagem do logo, se existir
let logoImage = null;

// Tentar importar de assets (processado pelo webpack)
try {
  logoImage = require('../assets/logo.png');
} catch (e) {
  // Logo não encontrado em assets, vai usar de public
}

// Logo de public (acessível via URL direta)
// Arquivos em public/ são servidos na raiz, então /logo.png funciona
const logoFromPublic = '/logo.png';

const Logo = ({ size = 'medium', showText = true, animated = false, useImage = false }) => {
  // TODOS OS HOOKS DEVEM ESTAR NO TOPO, SEM CONDIÇÕES
  const uniqueId = React.useMemo(() => Math.random().toString(36).substr(2, 9), []);
  const [imageError, setImageError] = React.useState(false);
  const [imageLoaded, setImageLoaded] = React.useState(false);
  
  // Calcular imageSrc sempre (fora do useEffect)
  const imageSrc = useImage ? (logoImage || logoFromPublic) : null;
  
  // useEffect sempre executado, com guard interno
  React.useEffect(() => {
    // Lógica condicional DENTRO do useEffect
    if (useImage && imageSrc && !imageError) {
      const img = new Image();
      img.src = imageSrc;
      img.onload = () => {
        setImageLoaded(true);
      };
      img.onerror = () => {
        setImageError(true);
      };
    }
  }, [useImage, imageSrc, imageError]);
  
  // Se useImage for true, tenta usar a imagem (de assets ou public)
  if (useImage) {
    return (
      <div className={`logo-container ${size} ${animated ? 'animated' : ''}`}>
        {!imageError && (
          <img 
            src={imageSrc} 
            alt="GEM - Sistema de Gestão de Ensino Musical" 
            className="logo-image"
            loading="eager"
            onLoad={() => setImageLoaded(true)}
            onError={() => {
              // Se falhar ao carregar, marca erro para mostrar SVG
              setImageError(true);
            }}
            style={{ 
              display: 'block',
              opacity: imageLoaded ? 1 : 0,
              transition: 'opacity 0.3s ease-in-out'
            }}
          />
        )}
        {imageError && (
          // Fallback SVG se imagem falhar
          <div className="logo-symbols">
            <svg className="logo-symbol logo-treble" viewBox="0 0 80 120" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <linearGradient id={`blueLight-${uniqueId}`} x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor="#3f51b5" />
                  <stop offset="50%" stopColor="#5c6bc0" />
                  <stop offset="100%" stopColor="#3f51b5" />
                </linearGradient>
              </defs>
              <path
                d="M 40 20 Q 30 35 30 55 Q 30 75 45 85 Q 30 95 30 110"
                stroke={`url(#blueLight-${uniqueId})`}
                strokeWidth="6"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <circle cx="30" cy="55" r="5" fill={`url(#blueLight-${uniqueId})`} />
            </svg>
          </div>
        )}
        {showText && imageError && (
          <div className="logo-text">
            <span className="logo-text-sis">Sis</span>
            <span className="logo-text-gem">GEM</span>
          </div>
        )}
      </div>
    );
  }
  
  // Caso contrário, usa o SVG padrão
  return (
    <div className={`logo-container ${size} ${animated ? 'animated' : ''}`}>
      <div className="logo-symbols">
        {/* Treble Clef (simplified and elegant) */}
        <svg className="logo-symbol logo-treble" viewBox="0 0 80 120" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <linearGradient id={`blueLight-${uniqueId}`} x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#3f51b5" />
              <stop offset="50%" stopColor="#5c6bc0" />
              <stop offset="100%" stopColor="#3f51b5" />
            </linearGradient>
          </defs>
          <path
            d="M 40 20 Q 30 35 30 55 Q 30 75 45 85 Q 30 95 30 110"
            stroke={`url(#blueLight-${uniqueId})`}
            strokeWidth="6"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <circle cx="30" cy="55" r="5" fill={`url(#blueLight-${uniqueId})`} />
        </svg>
      </div>
      {showText && (
        <div className="logo-text">
          <span className="logo-text-sis">Sis</span>
          <span className="logo-text-gem">GEM</span>
        </div>
      )}
    </div>
  );
};

// PropTypes para corrigir warnings
Logo.propTypes = {
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  showText: PropTypes.bool,
  animated: PropTypes.bool,
  useImage: PropTypes.bool
};

export default Logo;
